import React, { useState, useEffect } from 'react';
import { WorkLocation } from '../types';
import { getAllWorkLocations, createWorkLocation, updateWorkLocation, deleteWorkLocation } from '../firebase/firestore';
import { formatTurkeyDate, getCoordinatesFromAddress } from '../utils';

const AdminWorkLocations: React.FC = () => {
  const [workLocations, setWorkLocations] = useState<WorkLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    latitude: '',
    longitude: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [gettingCoordinates, setGettingCoordinates] = useState(false);

  useEffect(() => {
    loadWorkLocations();
  }, []);

  const loadWorkLocations = async () => {
    try {
      setLoading(true);
      const locations = await getAllWorkLocations();
      // Additional validation to ensure all locations have valid data
      const validLocations = locations.filter(location => 
        location && 
        typeof location.latitude === 'number' && 
        typeof location.longitude === 'number' &&
        !isNaN(location.latitude) && 
        !isNaN(location.longitude)
      );
      setWorkLocations(validLocations);
    } catch (error) {
      console.error('Error loading work locations:', error);
      setWorkLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGetCoordinates = async () => {
    if (!formData.address) {
      alert('Lütfen önce adres girin');
      return;
    }

    setGettingCoordinates(true);
    try {
      console.log('Getting coordinates for address:', formData.address);
      const coords = await getCoordinatesFromAddress(formData.address);
      console.log('Received coordinates:', coords);
      
      setFormData(prev => ({
        ...prev,
        latitude: coords.latitude.toString(),
        longitude: coords.longitude.toString()
      }));
    } catch (error) {
      console.error('Error getting coordinates:', error);
      const errorMessage = error instanceof Error ? error.message : 'Bilinmeyen hata';
      alert(`Koordinatlar alınırken hata oluştu: ${errorMessage}\n\nLütfen adresi kontrol edin ve tekrar deneyin.`);
    } finally {
      setGettingCoordinates(false);
    }
  };

  const handleCreateLocation = async () => {
    if (!formData.name || !formData.address || !formData.latitude || !formData.longitude) {
      alert('Lütfen tüm gerekli alanları doldurun');
      return;
    }

    setSubmitting(true);
    try {
      const locationData: any = {
        name: formData.name,
        address: formData.address,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude)
      };

      await createWorkLocation(locationData);

      // Reset form and reload
      setFormData({ name: '', address: '', latitude: '', longitude: '' });
      setShowCreateForm(false);
      await loadWorkLocations();
    } catch (error) {
      console.error('Error creating work location:', error);
      alert('İş yeri konumu oluşturulurken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateLocation = async (locationId: string) => {
    if (!formData.name || !formData.address || !formData.latitude || !formData.longitude) {
      alert('Lütfen tüm gerekli alanları doldurun');
      return;
    }

    setSubmitting(true);
    try {
      const locationData: any = {
        name: formData.name,
        address: formData.address,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude)
      };

      await updateWorkLocation(locationId, locationData);

      // Reset form and reload
      setFormData({ name: '', address: '', latitude: '', longitude: '' });
      setEditingLocation(null);
      await loadWorkLocations();
    } catch (error) {
      console.error('Error updating work location:', error);
      alert('İş yeri konumu güncellenirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteLocation = async (locationId: string) => {
    if (!confirm('Bu iş yeri konumunu silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await deleteWorkLocation(locationId);
      await loadWorkLocations();
    } catch (error) {
      console.error('Error deleting work location:', error);
      alert('İş yeri konumu silinirken hata oluştu');
    }
  };

  const startEditing = (location: WorkLocation) => {
    setEditingLocation(location.id);
    setFormData({
      name: location.name,
      address: location.address,
      latitude: location.latitude.toString(),
      longitude: location.longitude.toString()
    });
  };

  const cancelEditing = () => {
    setEditingLocation(null);
    setFormData({ name: '', address: '', latitude: '', longitude: '' });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-2">İş Yeri Konumları</h2>
          <p className="text-sm text-gray-600">Şirket iş yeri konumlarını yönetin</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
        >
          + Yeni Konum Ekle
        </button>
      </div>

      {/* Create/Edit Form */}
      {(showCreateForm || editingLocation) && (
        <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-md font-medium text-gray-900 mb-4">
            {editingLocation ? 'Konum Düzenle' : 'Yeni Konum Ekle'}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Konum Adı *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="Örn: Ana Ofis, Şube 1"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adres *
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="Örn: Atatürk Caddesi No:123, İstanbul"
                />
                <button
                  type="button"
                  onClick={handleGetCoordinates}
                  disabled={gettingCoordinates || !formData.address}
                  className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
                  title="Adresten koordinatları otomatik al"
                >
                  {gettingCoordinates ? '...' : '📍'}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                💡 Adres girdikten sonra 📍 butonuna tıklayarak koordinatları otomatik alabilirsiniz
              </p>
              <p className="mt-1 text-xs text-gray-400">
                ⚠️ Eğer otomatik koordinat alma çalışmazsa, koordinatları manuel olarak girebilirsiniz
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Enlem *
              </label>
              <input
                type="number"
                step="any"
                value={formData.latitude}
                onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="Örn: 41.0082"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Boylam *
              </label>
              <input
                type="number"
                step="any"
                value={formData.longitude}
                onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="Örn: 28.9784"
              />
            </div>
          </div>
          
          <div className="flex space-x-3 mt-4">
            <button
              onClick={editingLocation ? () => handleUpdateLocation(editingLocation) : handleCreateLocation}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
            >
              {submitting ? 'Kaydediliyor...' : (editingLocation ? 'Güncelle' : 'Oluştur')}
            </button>
            <button
              onClick={editingLocation ? cancelEditing : () => setShowCreateForm(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              İptal
            </button>
          </div>
        </div>
      )}

      {/* Work Locations List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {workLocations.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Henüz konum eklenmemiş</h3>
            <p className="text-sm text-gray-500">İş yeri konumları eklendiğinde burada görünecek</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Konum Adı
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Adres
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Koordinatlar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Oluşturulma Tarihi
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {workLocations.map((location) => (
                  <tr key={location.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{location.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{location.address}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div>Enlem: {typeof location.latitude === 'number' ? location.latitude.toFixed(6) : 'Geçersiz'}</div>
                        <div>Boylam: {typeof location.longitude === 'number' ? location.longitude.toFixed(6) : 'Geçersiz'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTurkeyDate(location.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => startEditing(location)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Düzenle
                        </button>
                        <button
                          onClick={() => handleDeleteLocation(location.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminWorkLocations; 